import * as React from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface FormSectionCardProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Presentational wrapper for the always-open contract-form sections.
 * Renders a bordered card with a title (+ optional leading icon) and a
 * one-line description, then the section's field grid as children.
 *
 * Purely visual — owns NO form state, validation, or field wiring. Those
 * stay in the individual section components. Keeps the section heading as
 * an <h3> with the same i18n text so existing queries/tests still match.
 */
export function FormSectionCard({
  title,
  description,
  icon,
  children,
  className,
}: FormSectionCardProps) {
  return (
    <Card className={cn("p-6", className)}>
      <div className="mb-5">
        <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
          {icon ? (
            <span className="text-muted-foreground" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          {title}
        </h3>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </Card>
  );
}
